import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Canned response picker per CLAUDE.md §22.1.
 *
 * Drops into the ticket comment composer (or anywhere agent text input
 * happens). Opens a search-filtered list of snippets ordered by
 * usage_count DESC. On pick:
 *   1. Calls POST /canned-responses/:id/use to increment usage_count
 *      so the next picker session surfaces frequently-used snippets first.
 *   2. Strips HTML to plain text (the current comment composer is a
 *      plain Textarea, not rich-text). Appends to existing body on a
 *      new line, OR replaces an empty body — chosen so an agent who
 *      pre-typed context doesn't lose it.
 *
 * Merge-tag substitution: simple find/replace of known placeholders the
 * snippet author may have included. The caller supplies what it knows
 * about the ticket context.
 */
import { useQuery, useMutation } from '@tanstack/react-query';
import { ChevronDown, MessageSquare, Search, Sparkles, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
/** Strip HTML to plain text — the canned response is authored as HTML
 *  (so it survives a future rich-text composer rollout) but the current
 *  Textarea expects plain text. */
function htmlToText(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    // Convert block-level tags to newlines for readability.
    tmp.querySelectorAll('br').forEach((br) => br.replaceWith('\n'));
    tmp.querySelectorAll('p, div, li').forEach((el) => {
        el.insertAdjacentText('beforebegin', '\n');
    });
    return (tmp.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
}
/** Replace every `{{key}}` token using the supplied value map.
 *  Tokens with no match are left intact so the agent edits them in place. */
function applyMergeTags(text, values) {
    return text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (m, key) => {
        // Explicit narrowing: the indexed access is `string | undefined`
        // under noUncheckedIndexedAccess, but the hasOwnProperty guard
        // ensures it's actually a string at this branch.
        if (Object.prototype.hasOwnProperty.call(values, key)) {
            return values[key] ?? m;
        }
        return m;
    });
}
export function CannedResponsePicker({ currentBody, onInsert, mergeContext, }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const wrapRef = useRef(null);
    // Click outside to close.
    useEffect(() => {
        if (!open)
            return;
        const onDoc = (e) => {
            if (!wrapRef.current?.contains(e.target))
                setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);
    // Esc to close.
    useEffect(() => {
        if (!open)
            return;
        const onKey = (e) => { if (e.key === 'Escape')
            setOpen(false); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);
    const list = useQuery({
        queryKey: ['canned-responses', query],
        queryFn: () => api.get(`/canned-responses${query ? `?q=${encodeURIComponent(query)}` : ''}`),
        enabled: open,
        staleTime: 30_000,
    });
    const use = useMutation({
        mutationFn: (id) => api.post(`/canned-responses/${id}/use`),
    });
    const pick = async (cr) => {
        // Increment usage_count server-side — fire-and-forget; UI doesn't wait
        // because the user clicking should feel instant.
        use.mutate(cr.id);
        let text = htmlToText(cr.body_html);
        if (mergeContext)
            text = applyMergeTags(text, mergeContext.values);
        const next = currentBody.trim() ? `${currentBody.replace(/\s+$/, '')}\n\n${text}` : text;
        onInsert(next);
        setOpen(false);
        setQuery('');
    };
    return (_jsxs("div", { ref: wrapRef, className: "relative", children: [_jsxs(Button, { type: "button", variant: "ghost", size: "sm", onClick: () => setOpen((o) => !o), "aria-haspopup": "listbox", "aria-expanded": open, title: "Insert a saved snippet (canned response)", children: [_jsx(Sparkles, { size: 14 }), "Snippets", _jsx(ChevronDown, { size: 12, className: cn('transition-transform', open && 'rotate-180') })] }), open && (_jsxs("div", { className: "absolute right-0 z-30 mt-1 w-[360px] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-900", children: [_jsx("div", { className: "border-b border-slate-200 px-3 py-2 dark:border-slate-800", children: _jsxs("div", { className: "relative", children: [_jsx(Search, { className: "pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" }), _jsx(Input, { autoFocus: true, value: query, onChange: (e) => setQuery(e.target.value), placeholder: "Search snippets\u2026", className: "pl-8 pr-7" }), query && (_jsx("button", { type: "button", onClick: () => setQuery(''), "aria-label": "Clear search", className: "absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700", children: _jsx(X, { size: 12 }) }))] }) }), _jsxs("div", { className: "max-h-72 overflow-y-auto", children: [list.isLoading && (_jsx("div", { className: "px-3 py-6 text-center text-xs text-slate-500", children: "Loading\u2026" })), !list.isLoading && (list.data?.length ?? 0) === 0 && (_jsxs("div", { className: "px-3 py-6 text-center text-xs text-slate-500", children: [_jsx(MessageSquare, { className: "mx-auto mb-2 h-5 w-5 text-slate-400" }), query ? `No snippets match "${query}".` : 'No snippets yet. Create one via the API.'] })), _jsx("ul", { className: "divide-y divide-slate-100 dark:divide-slate-800", children: (list.data ?? []).map((cr) => (_jsx("li", { children: _jsx("button", { type: "button", onClick: () => pick(cr), className: "block w-full px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800", children: _jsxs("div", { className: "flex items-center justify-between gap-2", children: [_jsxs("div", { className: "min-w-0 flex-1", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-sm font-medium text-slate-800 dark:text-slate-200", children: cr.name }), cr.category && (_jsx("span", { className: "rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-400", children: cr.category })), cr.scope === 'user' && (_jsx("span", { className: "rounded-full bg-brand-100 px-2 py-0.5 text-[10px] font-medium text-brand-700 dark:bg-brand-950/40 dark:text-brand-300", children: "private" }))] }), _jsx("div", { className: "mt-0.5 line-clamp-1 text-xs text-slate-500", children: htmlToText(cr.body_html).slice(0, 80) })] }), _jsx("div", { className: "shrink-0 text-[10px] tabular-nums text-slate-400", title: "Times used", children: cr.usage_count })] }) }) }, cr.id))) })] }), _jsxs("div", { className: "border-t border-slate-200 px-3 py-1.5 text-[10px] text-slate-400 dark:border-slate-800", children: ["Tip: snippets can include ", _jsx("code", { className: "rounded bg-slate-100 px-1 dark:bg-slate-800", children: '{{ticket.requester.first_name}}' }), " \u2014 replaced when inserted."] })] }))] }));
}
